
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";

import { ComprehensiveErrorBoundary } from "@/components/ErrorHandling/ComprehensiveErrorBoundary";
import SEO from "@/components/SEO/SEO";
import { DevTools } from "@/components/dev";
import { OptimizedContextProvider } from "@/utils/contextOptimizer";
import ProtectedRoute from "@/components/Auth/ProtectedRoute";
import AdminRouteGuard from "@/components/Admin/AdminRouteGuard";
import { isNativeApp } from "@/lib/nativeApp";
import { initStatusBar } from "@/lib/nativeStatusBar";
import { initNativeRealtimeLifecycle } from "@/lib/nativeRealtimeLifecycle";
import { initRealtimeIdleSocket, stopRealtimeIdleSocket } from "@/lib/realtimeIdleSocket";
import { initNativeKeyboard } from "@/lib/nativeKeyboard";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { supabase } from "@/integrations/supabase/client";
import { isLinkingInProgress, clearLinkingIntent, debugLinkingIntentSnapshot } from "@/utils/linkingIntent";
import { forceRealtimeReconnect } from "@/lib/realtimeReconnect";

// Lazy-loaded pages for better performance
import {
  LazyIndex,
  LazyPersonaSelection,
  LazyDashboardPage,
  
  LazyLogin,
  LazySignupPage,
  LazyWrapper,
} from "@/components/Performance/LazyRoutes";

// Static import for 404 — must never fail to load
import NotFoundPage from "@/pages/NotFoundPage";

// Lazy-loaded secondary pages (rarely visited)
import React, { lazy, Suspense, useEffect, useRef } from "react";
import { captureError } from "@/utils/captureError";

const ComprehensiveLegal = lazy(() => import("@/pages/ComprehensiveLegal"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const ResetPage = lazy(() => import("@/pages/ResetPage"));

// V2 preview-only experimental landing (gated; never indexed).
const V2Guard = lazy(() => import("@/components/v2/V2Guard"));
const V2Landing = lazy(() => import("@/pages/v2/V2Landing"));

// Small helper so /v2/signup/:persona forwards to /signup/:persona with the actual value.
const V2SignupRedirect: React.FC = () => {
  const { persona } = useParams();
  return <Navigate to={persona ? `/signup/${persona}` : "/signup"} replace />;
};
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const ResetPasswordByCnic = lazy(() => import("@/pages/ResetPasswordByCnic"));
const BrandAssetsPage = lazy(() => import("@/pages/BrandAssetsPage"));
const PublicProfilePage = lazy(() => import("@/pages/PublicProfilePage"));
const PublicJobPage = lazy(() => import("@/pages/PublicJobPage"));
const JoinTeam = lazy(() => import("@/pages/JoinTeam"));
const LazySafetyPage = lazy(() => import("@/pages/SafetyPage"));
// PaymentGatewayTest is gated to dev + Lovable preview + behtari.pk so it stays
// out of unknown public builds but remains reachable for admin verification.
const ALLOW_GATEWAY_TEST =
  import.meta.env.DEV ||
  (typeof window !== "undefined" &&
    /(?:lovable\.app|behtari\.pk|localhost)$/.test(window.location.hostname));
const PaymentGatewayTest = ALLOW_GATEWAY_TEST
  ? lazy(() => import("@/pages/admin/PaymentGatewayTest"))
  : null;
const PaymentReturn = lazy(() => import("@/pages/PaymentReturn"));
const AccountDeletionPage = lazy(() => import("@/pages/AccountDeletionPage"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));

// PWA handlers — file_handlers / protocol_handlers / share_target entry points
const FileHandler = lazy(() => import("@/pages/handlers/FileHandler"));
const DeepLinkHandler = lazy(() => import("@/pages/handlers/DeepLinkHandler"));
const ShareReceiver = lazy(() => import("@/pages/handlers/ShareReceiver"));

// Public SEO sub-routes — lazy-loaded so /dashboard + / payloads are unchanged.
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const HowItWorksPage = lazy(() => import("@/pages/public/HowItWorksPage"));
const FAQsPage = lazy(() => import("@/pages/public/FAQsPage"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));
const ForWorkersPage = lazy(() => import("@/pages/public/ForWorkersPage"));
const ForEmployersPage = lazy(() => import("@/pages/public/ForEmployersPage"));
const ForBusinessesPage = lazy(() => import("@/pages/public/ForBusinessesPage"));
const ForAgenciesPage = lazy(() => import("@/pages/public/ForAgenciesPage"));
const CityHubPage = lazy(() => import("@/pages/public/CityHubPage"));

const WhatsAppStyleChatBubble = lazy(() => import("@/components/Chat/WhatsAppStyleChatBubble"));
const VoiceAssistantButton = lazy(() => import("@/components/VoiceAssistant/VoiceAssistantButton"));
const OfflineQueueIndicator = lazy(() => import("@/components/PWA/OfflineQueueIndicator"));
const OAuthProfileNudge = lazy(() => import("@/components/Profile/OAuthProfileNudge"));

// Consolidated CSS - Single import for all styles
import "./styles/main.css";

import SkipNav from "@/components/Accessibility/SkipNav";

import { FontSizeProvider } from "@/contexts/FontSizeContext";
import { SimpleModeProvider } from "@/contexts/SimpleModeContext";
import { AccessibilityModeProvider } from "@/contexts/AccessibilityModeContext";

import { useUser } from "@/contexts/UserContext";

import ScrollToTop from "@/components/Layout/ScrollToTop";
import NetworkBanner from "@/components/Layout/NetworkBanner";
import UpdateAvailableBanner from "@/components/Layout/UpdateAvailableBanner";



/**
 * Mount-site auth gate for the signed-in-only floating helpers.
 *
 * Must be its own component: App is where the provider tree is CREATED, so it
 * cannot call useUser() itself — this renders below the providers, where the
 * context exists.
 *
 * `isAuthenticated === false` covers both "signed out" and "still hydrating".
 * Deferring the fetch until auth resolves costs a signed-in user nothing
 * visible: the Suspense fallback is already `null`, so the bubble simply
 * appears when its chunk lands, exactly as it does today.
 */
const AuthedFloatingHelpers = () => {
  const { isAuthenticated } = useUser();
  if (!isAuthenticated) return null;

  return (
    <>
      <ComprehensiveErrorBoundary level="component" showToast={false}>
        <Suspense fallback={null}><WhatsAppStyleChatBubble /></Suspense>
      </ComprehensiveErrorBoundary>
      <ComprehensiveErrorBoundary level="component" showToast={false}>
        <Suspense fallback={null}><VoiceAssistantButton /></Suspense>
      </ComprehensiveErrorBoundary>
      <ComprehensiveErrorBoundary level="component" showToast={false}>
        <Suspense fallback={null}><OAuthProfileNudge /></Suspense>
      </ComprehensiveErrorBoundary>
    </>
  );
};

const App = () => {
  const navigate = useNavigate();

  // Android hardware back button: navigate back within the SPA history, or exit
  // the app at the root. Native-only — inert on web (no listener registered).
  useEffect(() => {
    if (!isNativeApp()) return;
    let listener: Awaited<ReturnType<typeof CapacitorApp.addListener>> | null = null;
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapacitorApp.exitApp();
      }
    }).then(l => { listener = l; });
    // Foreground resume: force a hard realtime reconnect. Android commonly
    // kills the websocket while backgrounded, and phoenix can believe a dead
    // socket is still connected — nativeRealtimeLifecycle's conditional
    // connect() is a no-op in that state, so this hard cycle is the recovery.
    let stateChangeListener: Awaited<ReturnType<typeof CapacitorApp.addListener>> | null = null;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        forceRealtimeReconnect('app-resumed');
      }
    }).then(l => { stateChangeListener = l; });
    return () => { listener?.remove(); stateChangeListener?.remove(); };
  }, []);

  // Keep the latest navigate in a ref so the appUrlOpen listener below can be
  // registered exactly once for the app's lifetime (empty deps) without going
  // stale. Re-registering on every navigate() identity change risked leaking a
  // second listener (see the async-cleanup race guarded below), which would
  // double-fire exchangeCodeForSession(code) on the SAME one-time code — the
  // second call fails ("code already used") and could bounce a mid-flight
  // sign-in to /login, matching the reported "hung / retry error" symptom.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // OAuth deep-link handler — fires when Android returns from Google sign-in
  // via com.behtari.app://login?code=... (PKCE) or #access_token=... (implicit)
  useEffect(() => {
    if (!isNativeApp()) return;

    let handle: { remove: () => void } | null = null;
    // If the effect is torn down before addListener's promise resolves, the
    // cleanup below runs with handle === null and cannot remove anything. This
    // flag lets the resolved handle remove itself immediately, so we never
    // leak a duplicate listener.
    let cancelled = false;

    CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url.startsWith('com.behtari.app://')) return;
      const nav = navigateRef.current;

      // Close the Chrome Custom Tab that hosted the Google sign-in
      try { await Browser.close(); } catch { /* ignore */ }

      // Parse the URL — normalize the custom scheme so URL() can parse it
      const normalized = url.replace('com.behtari.app://', 'https://placeholder.invalid/');
      let parsed: URL;
      try {
        parsed = new URL(normalized);
      } catch (parseErr) {
        captureError('oauth-native', parseErr, { stage: 'url-parse', url });
        nav('/login');
        return;
      }

      const errorCode = parsed.searchParams.get('error') || new URLSearchParams(parsed.hash.slice(1)).get('error');
      if (errorCode) {
        // Provider-returned error (user cancelled, consent denied, etc.). Not an
        // exception, but log it so a repeat is visible.
        const wasLinking = isLinkingInProgress();
        // Drop any in-flight linking session so a cancelled link can't hijack a
        // later sign-in; route a cancelled link back to Settings, not /login.
        if (wasLinking) clearLinkingIntent();
        captureError(wasLinking ? 'oauth-link-native' : 'oauth-native', new Error(`provider returned error=${errorCode}`), { stage: 'provider-error', errorCode });
        nav(wasLinking ? '/dashboard/settings' : '/login', { replace: true });
        return;
      }

      // Diagnostic trace — fires only when there is SOME trace (valid or
      // otherwise) of a linking attempt, so it never fires on ordinary native
      // logins. Distinguishes "flag never made it into storage" from "flag
      // present but read as invalid" from "flag valid" at the exact point
      // App.tsx decides whether this deep link is a linking return or a
      // normal login — the fork point a real device test (2026-07-27,
      // Majid Ali Waryam) produced zero telemetry for.
      {
        const linkSnapshot = debugLinkingIntentSnapshot();
        if (linkSnapshot.sessionStorageHasFlag || linkSnapshot.localStorageHasFlag || linkSnapshot.isValid) {
          captureError('oauth-link-native', new Error('appUrlOpen deep-link received'), {
            stage: 'deep-link-received',
            isLinkingInProgress: linkSnapshot.isValid,
            linkingFlagAgeMs: linkSnapshot.ageMs,
            sessionStorageHasFlag: linkSnapshot.sessionStorageHasFlag,
            localStorageHasFlag: linkSnapshot.localStorageHasFlag,
            hasCode: !!parsed.searchParams.get('code'),
          });
        }
      }

      // Account-linking return (Settings → Connect Google/Apple). A linking
      // session persisted via linkingIntent means this deep-link is a "link my
      // existing account" return, NOT a normal login. Exchange the code to
      // materialise the OAuth session — the global onAuthStateChange SIGNED_IN
      // handler (useAuthState) then owns completeSocialLink() exactly as it does
      // on web (restore original session + call link-social-account), so we do
      // NOT call it here too (that would race two concurrent session mutations).
      // We only route to /dashboard/settings instead of /auth/callback.
      if (isLinkingInProgress()) {
        const linkCode = parsed.searchParams.get('code');
        // Implicit-flow tokens, read the SAME way the normal-login branch below
        // does. This is the fix for the 2026-07-29 root cause: the Supabase
        // client is NOT configured with `flowType: 'pkce'`, so every native
        // OAuth return — login AND linking alike — comes back as
        // `com.behtari.app://login#access_token=…&refresh_token=…`, never with
        // a `?code=`. The login branch has always had this implicit fallback;
        // the linking branch was written PKCE-only, so it took the "no code"
        // path on EVERY attempt, dropped the flag, and left the user back on
        // Settings with nothing linked (7 captured occurrences, one user,
        // 2026-07-27 → 2026-07-29). Handling both shapes here — not switching
        // the client's flow type — keeps the two branches symmetric and touches
        // no locked auth artifact.
        const linkHash = new URLSearchParams(parsed.hash.slice(1));
        const linkAccessToken = linkHash.get('access_token');
        const linkRefreshToken = linkHash.get('refresh_token');

        if (linkCode) {
          try {
            const { data: linkExchangeData, error: linkExchangeApiErr } = await supabase.auth.exchangeCodeForSession(linkCode);
            if (linkExchangeApiErr) throw linkExchangeApiErr;
            captureError('oauth-link-native', new Error('exchangeCodeForSession succeeded (linking return)'), {
              stage: 'exchange-success',
              resultUserId: linkExchangeData?.session?.user?.id,
              resultProvider: linkExchangeData?.session?.user?.app_metadata?.provider,
            });
          } catch (linkExchangeErr) {
            clearLinkingIntent();
            captureError('oauth-link-native', linkExchangeErr, { stage: 'exchangeCodeForSession-link' });
          }
        } else if (linkAccessToken && linkRefreshToken) {
          // Materialise the OAuth session from the hash. As with the code path,
          // the global onAuthStateChange SIGNED_IN handler (useAuthState) then
          // owns completeSocialLink() — we must NOT call it here as well, and
          // must NOT clear the linking intent on success, or that handler would
          // never see the in-flight flag.
          try {
            const { data: linkSetData, error: linkSetErr } = await supabase.auth.setSession({
              access_token: linkAccessToken,
              refresh_token: linkRefreshToken,
            });
            if (linkSetErr) throw linkSetErr;
            captureError('oauth-link-native', new Error('setSession succeeded (implicit linking return)'), {
              stage: 'set-session-success',
              resultUserId: linkSetData?.session?.user?.id,
              resultProvider: linkSetData?.session?.user?.app_metadata?.provider,
            });
          } catch (linkSetErr) {
            clearLinkingIntent();
            captureError('oauth-link-native', linkSetErr, { stage: 'setSession-link' });
          }
        } else {
          // Neither shape present — nothing to act on; drop the flag so the
          // listener doesn't try to complete against a missing OAuth session.
          // NOTE: the raw `url` is deliberately NOT logged here. On the implicit
          // flow it carries a live access_token / provider_token in the hash,
          // and the previous version of this capture shipped exactly that into
          // PostHog on every failure.
          clearLinkingIntent();
          captureError('oauth-link-native', new Error('linking deep link had no code or tokens'), {
            stage: 'no-payload-link',
            deepLinkPath: parsed.pathname,
            hasQueryParams: parsed.search.length > 1,
            hasHash: parsed.hash.length > 1,
          });
        }
        nav('/dashboard/settings', { replace: true });
        return;
      }

      // PKCE flow: exchange the one-time code for a session (most common with Supabase v2+)
      const code = parsed.searchParams.get('code');
      if (code) {
        try {
          await supabase.auth.exchangeCodeForSession(code);
          nav('/auth/callback', { replace: true });
        } catch (exchangeErr) {
          captureError('oauth-native', exchangeErr, { stage: 'exchangeCodeForSession' });
          nav('/login');
        }
        return;
      }

      // Implicit flow fallback: tokens already in the URL hash
      const hash = new URLSearchParams(parsed.hash.slice(1));
      const accessToken = hash.get('access_token');
      const refreshToken = hash.get('refresh_token');
      if (accessToken && refreshToken) {
        try {
          await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          nav('/auth/callback', { replace: true });
        } catch (setSessionErr) {
          captureError('oauth-native', setSessionErr, { stage: 'setSession' });
          nav('/login');
        }
        return;
      }

      // Deep link arrived with neither a code nor tokens nor an error — nothing
      // to act on. Log it (this should never happen) and fall back to login.
      // As above: never log the raw deep-link URL — a partial implicit return
      // (e.g. access_token present but refresh_token missing) would reach here
      // with a live token in the hash.
      captureError('oauth-native', new Error('deep link had no code/tokens/error'), {
        stage: 'no-payload',
        deepLinkPath: parsed.pathname,
        hasQueryParams: parsed.search.length > 1,
        hasHash: parsed.hash.length > 1,
      });
      nav('/login');
    }).then(h => {
      handle = h;
      if (cancelled) handle.remove();
    });

    return () => { cancelled = true; handle?.remove(); };
  }, []);

  // Initialise native status bar on first mount — disables WebView overlay
  // so the phone's clock/battery/notifications are always visible.
  useEffect(() => {
    void initStatusBar();
  }, []);

  // Native soft-keyboard lifecycle: tags <body> with `keyboard-open` so fixed
  // bottom chrome ([data-keyboard-avoid]) hides instead of covering focused
  // inputs while typing. No-op on web.
  useEffect(() => {
    void initNativeKeyboard();
  }, []);

  // Native realtime lifecycle: token auto-refresh + websocket reconnect on
  // resume/network-restore, plus socket close-code telemetry (Items 8+10).
  useEffect(() => {
    initNativeRealtimeLifecycle();
  }, []);

  // Close the realtime websocket once no channel has been joined for a while.
  // Phoenix keeps the socket and its reconnect timer alive after the last
  // channel is removed, so a signed-out or realtime-free screen leaves it
  // retrying forever on a failing transport (24% of realtime close events
  // reported channelCount 0, across more distinct users than the in-use cases).
  // Not native-gated: it uses no native API, and the leak exists on web too.
  useEffect(() => {
    initRealtimeIdleSocket();
    return () => stopRealtimeIdleSocket();
  }, []);

  // SPA navigation requested by a native push-notification tap
  // (nativePush.ts dispatches this instead of hard-reloading the WebView).
  useEffect(() => {
    if (!isNativeApp()) return;
    const onPushNavigate = (e: Event) => {
      const url = (e as CustomEvent<{ url?: string }>).detail?.url;
      if (url && url.startsWith('/')) navigateRef.current(url);
    };
    window.addEventListener('behtari:push-navigate', onPushNavigate);
    return () => window.removeEventListener('behtari:push-navigate', onPushNavigate);
  }, []);

  return (
    <OptimizedContextProvider>
      <FontSizeProvider>
      <SimpleModeProvider>
      <AccessibilityModeProvider>
      <ComprehensiveErrorBoundary level="page" showToast={true}>
        <SkipNav />
        <Sonner />
        <NetworkBanner />
        <UpdateAvailableBanner />
        {/* Default site-wide SEO. Per-page <SEO> overrides cleanly via Helmet. */}
        <SEO description="Connect with verified workers and employers across Pakistan. AI-powered matching, secure trials, fair wages." />
        <LazyWrapper>
          <div id="main-content">
          <ScrollToTop />
            
            <Routes>
              <Route path="/" element={isNativeApp() ? <Navigate to="/login" replace /> : <LazyIndex />} />
              <Route path="/persona-selection" element={<LazyPersonaSelection />} />

              {/* V2 experimental preview — gated to preview hosts / unlocked users.
                  Signup + persona-selection reuse the existing (working) auth flows. */}
              <Route
                path="/v2"
                element={
                  <Suspense fallback={null}>
                    <V2Guard>
                      <V2Landing />
                    </V2Guard>
                  </Suspense>
                }
              />
              <Route path="/v2/persona-selection" element={<Navigate to="/persona-selection" replace />} />
              <Route path="/v2/signup" element={<Navigate to="/signup" replace />} />
              <Route path="/v2/signup/:persona" element={<V2SignupRedirect />} />

              {/* Public SEO sub-routes — split from CompleteLandingPage so each
                  topic gets its own crawlable URL, H1, canonical, and JSON-LD.
                  Content is reused verbatim from the landing-page section
                  components — zero new copy, zero design changes to "/". */}
              <Route path="/about" element={<Suspense fallback={null}><AboutPage /></Suspense>} />
              <Route path="/how-it-works" element={<Suspense fallback={null}><HowItWorksPage /></Suspense>} />
              <Route path="/faqs" element={<Suspense fallback={null}><FAQsPage /></Suspense>} />
              <Route path="/faq" element={<Navigate to="/faqs" replace />} />
              <Route path="/contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />
              <Route path="/for-workers" element={<Suspense fallback={null}><ForWorkersPage /></Suspense>} />
              <Route path="/for-employers" element={<Suspense fallback={null}><ForEmployersPage /></Suspense>} />
              <Route path="/for-businesses" element={<Suspense fallback={null}><ForBusinessesPage /></Suspense>} />
              <Route path="/for-agencies" element={<Suspense fallback={null}><ForAgenciesPage /></Suspense>} />
              <Route path="/cities/:city" element={<Suspense fallback={null}><CityHubPage /></Suspense>} />


              {/* Canonical admin payment gateway test — DEV ONLY (env-gated for store builds) */}
              {/* Canonical admin payment gateway test — gated to dev + Lovable preview + behtari.pk */}
              {ALLOW_GATEWAY_TEST && PaymentGatewayTest && (
                <Route path="/dashboard/payment-gateway-test" element={
                  <ProtectedRoute requireAuth skipPersonaCheck>
                    <AdminRouteGuard allowAdmin>
                      <Suspense fallback={null}><PaymentGatewayTest /></Suspense>
                    </AdminRouteGuard>
                  </ProtectedRoute>
                } />
              )}
              {/* Dashboard Routes - Protected */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute requireAuth>
                  <LazyDashboardPage />
                </ProtectedRoute>
              } />
              {/* Legacy alias — redirect to canonical dashboard route on allowed hosts */}
              {ALLOW_GATEWAY_TEST && (
                <Route path="/admin/payment-gateway-test" element={<Navigate to="/dashboard/payment-gateway-test" replace />} />
              )}
              <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
              <Route path="/payment/return" element={<Suspense fallback={null}><PaymentReturn /></Suspense>} />
             <Route path="/login" element={<LazyLogin />} />
             <Route path="/signup" element={<LazySignupPage />} />
             <Route path="/signup/:persona" element={<LazySignupPage />} />
             <Route path="/auth/callback" element={<Suspense fallback={null}><AuthCallback /></Suspense>} />
             <Route path="/.lovable/oauth/consent" element={<Suspense fallback={null}><OAuthConsent /></Suspense>} />
              <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
              <Route path="/onboarding/:persona" element={<Navigate to="/dashboard" replace />} />
              
              {/* Public profile view */}
              <Route path="/profile/:id" element={
                <ProtectedRoute requireAuth>
                  <Suspense fallback={null}><PublicProfilePage /></Suspense>
                </ProtectedRoute>
              } />

              {/* Legacy route redirects — cached URLs from older versions.
                  Only kept where internal navigate() callers, manifest shortcuts,
                  push-notification deep links, or AI concierge action_urls reference them. */}
              <Route path="/messages" element={<Navigate to="/dashboard/messages" replace />} />
              <Route path="/wallet" element={<Navigate to="/dashboard/wallet" replace />} />
              <Route path="/jobs/:id" element={<Suspense fallback={null}><PublicJobPage /></Suspense>} />
              <Route path="/jobs" element={<Navigate to="/dashboard/jobs" replace />} />
              <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
              <Route path="/settings" element={<Navigate to="/dashboard/settings" replace />} />
              <Route path="/notifications" element={<Navigate to="/dashboard/notifications" replace />} />
              <Route path="/help" element={<Navigate to="/dashboard/help" replace />} />
              <Route path="/safety" element={<Suspense fallback={null}><LazySafetyPage /></Suspense>} />
              <Route path="/join-team" element={<Suspense fallback={null}><JoinTeam /></Suspense>} />
              <Route path="/team" element={<Navigate to="/dashboard/team" replace />} />
              <Route path="/clients" element={<Navigate to="/dashboard/clients" replace />} />
               
              {/* Legal Pages */}
              <Route path="/legal" element={<Suspense fallback={null}><ComprehensiveLegal /></Suspense>} />
              <Route path="/legal/:type" element={<Suspense fallback={null}><ComprehensiveLegal /></Suspense>} />
              <Route path="/privacy-policy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
              <Route path="/terms-of-service" element={<Suspense fallback={null}><TermsOfServicePage /></Suspense>} />
              
              {/* Cache Reset */}
              <Route path="/reset" element={<Suspense fallback={null}><ResetPage /></Suspense>} />
              
              {/* Password Reset */}
              <Route path="/reset-password" element={<Suspense fallback={null}><ResetPasswordPage /></Suspense>} />
              <Route path="/reset-password-cnic" element={<Suspense fallback={null}><ResetPasswordByCnic /></Suspense>} />
              <Route path="/verify-email" element={<Suspense fallback={null}><VerifyEmail /></Suspense>} />
              <Route path="/accept-invite" element={<Suspense fallback={null}><AcceptInvite /></Suspense>} />
              <Route path="/brand-assets" element={<Suspense fallback={null}><BrandAssetsPage /></Suspense>} />

              {/* Public account deletion (Google Play policy — no auth required) */}
              <Route path="/account-deletion" element={<Suspense fallback={null}><AccountDeletionPage /></Suspense>} />
              <Route path="/delete-account" element={<Navigate to="/account-deletion" replace />} />
              <Route path="/data-deletion" element={<Navigate to="/account-deletion" replace />} />

              {/* PWA OS-level handlers — file open / custom protocol / share target */}
              <Route path="/handlers/file" element={
                <ProtectedRoute requireAuth>
                  <Suspense fallback={null}><FileHandler /></Suspense>
                </ProtectedRoute>
              } />
              <Route path="/handlers/deeplink" element={
                <ProtectedRoute requireAuth>
                  <Suspense fallback={null}><DeepLinkHandler /></Suspense>
                </ProtectedRoute>
              } />
              <Route path="/handlers/share" element={
                <ProtectedRoute requireAuth>
                  <Suspense fallback={null}><ShareReceiver /></Suspense>
                </ProtectedRoute>
              } />

              {/* 404 Not Found — static import, must never fail */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </LazyWrapper>
        
        
        {/* Floating helpers — each isolated so a stale chunk failure stays silent
            and never bubbles to the global error boundary (which would trigger
            a re-render cascade and toast spam). */}
        {/* Signed-in-only helpers are gated HERE, at the mount site, not just
            inside each component (2026-08-26).

            All three are already lazy(), but they were rendered unconditionally
            on every route — including the public landing page. A lazy boundary
            only defers the download until React MOUNTS the component; mounting
            it is what triggers the fetch. So an anonymous visitor to "/"
            downloaded and parsed each chunk purely so its first statement could
            be `if (!isAuthenticated) return null`.

            WhatsAppStyleChatBubble is the expensive one: it statically imports
            UniversalMessagesPanel, so its chunk drags in the whole authenticated
            messaging UI. Measured on the built bundle, anonymous "/" at 360x800:
              UniversalMessagesPanel  177.8 kB
              VoiceAssistantButton     36.4 kB
              WhatsAppStyleChatBubble   2.1 kB
              OAuthProfileNudge         1.5 kB
              total                   217.8 kB, rendering nothing.
            That is real money on a 3G connection, which is most of this
            product's traffic.

            The in-component guards are deliberately LEFT IN PLACE — they still
            own the rules this gate cannot express (the /messages route, the
            chat bubble's persona gate, VoiceAssistant's !isSupported, the
            nudge's own open/closed state). This only stops the chunk being
            fetched for a visitor who is definitively signed out.

            NOTE: "admin suppression" used to be in that list. It is gone as of
            2026-08-28 — the chat bubble now renders for EVERY persona, admin
            included, by product rule. See WhatsAppStyleChatBubble.

            OfflineQueueIndicator is intentionally NOT gated: the offline queue
            is meaningful before sign-in, and its chunk is small. */}
        <AuthedFloatingHelpers />
        <ComprehensiveErrorBoundary level="component" showToast={false}>
          <Suspense fallback={null}><OfflineQueueIndicator /></Suspense>
        </ComprehensiveErrorBoundary>
        
        
        {/* Development Tools - Only in Development Environment with URL parameter */}
        {import.meta.env.DEV && typeof window !== 'undefined' && window.location.search.includes('devtools=true') && <DevTools />}
      </ComprehensiveErrorBoundary>
      </AccessibilityModeProvider>
      </SimpleModeProvider>
      </FontSizeProvider>
    </OptimizedContextProvider>
  );
};

export default App;
